
import React, { useState, useEffect } from 'react';
import { Game } from '../types';
import { audioService } from '../services/audioService';
import FruitVortex from '../games/FruitVortex';
import WordBuilder from '../games/WordBuilder';
import Sudoku from '../games/Sudoku';
import MemoryMatrix from '../games/MemoryMatrix';
import Labyrinth from '../games/Labyrinth';
import ColorClash from '../games/ColorClash';
import BinaryDash from '../games/BinaryDash';
import BitMaster from '../games/BitMaster';
import QuickMath from '../games/QuickMath';
import PatternFinder from '../games/PatternFinder';
import GrammarGuardian from '../games/GrammarGuardian';
import ResonanceBreathing from '../games/ResonanceBreathing';
import ReactionTest from '../games/ReactionTest';
import Tetris from '../games/Tetris';
import NeonRacer from '../games/NeonRacer';
import SkyStrike from '../games/SkyStrike';
import SnakeArena from '../games/SnakeArena';
import Logo from './Logo';
import VictoryEffect from './VictoryEffect';

interface GameRunnerProps {
  game: Game;
  onClose: () => void;
  onSaveScore: (score: number, metadata?: any) => void;
  highScore: number;
  isDarkMode: boolean;
  isAnonymous: boolean;
  onOpenProfile: () => void;
  onViewLeaderboard: () => void;
  sfxVolume: number;
  hapticFeedback: boolean;
  globalRecord?: number;
  activeChallenge?: any | null;
}

const GameRunner: React.FC<GameRunnerProps> = ({ game, onClose, onSaveScore, highScore, isDarkMode, isAnonymous, onOpenProfile, onViewLeaderboard, sfxVolume, hapticFeedback, globalRecord, activeChallenge }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showGameOver, setShowGameOver] = useState(false);
  const [isVictory, setIsVictory] = useState(false);
  const [currentScore, setCurrentScore] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const handleScoreUpdate = React.useCallback((score: number, metadata?: any) => {
    setCurrentScore(score);
    onSaveScore(score, metadata);
  }, [onSaveScore]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data === 'exitToHub') {
        handleClose();
      }
      if (typeof event.data === 'object' && event.data.type === 'gameScore') {
        onSaveScore(event.data.score, event.data.metadata);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onClose, onSaveScore]);

  // Keyboard shortcut listener to pause/resume the game
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isPlaying || showGameOver) return;
      if (e.key === 'Escape' || e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        setIsPaused(prev => !prev);
        audioService.playClick();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPlaying, showGameOver]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, 400); 
  };

  const handleGameOver = React.useCallback((finalScore: number, victory: boolean = false, metadata?: any) => {
    setCurrentScore(finalScore);
    onSaveScore(finalScore, { ...metadata, final: true });
    setIsVictory(victory);
    setIsPlaying(false);
    setIsPaused(false);
    setShowGameOver(true);
    if (victory) audioService.playSuccess();
    else audioService.playError();
  }, [onSaveScore]);

  const handleRetry = () => {
    setCurrentScore(0);
    setShowGameOver(false);
    setIsVictory(false);
    setIsPlaying(true);
    setIsPaused(false);
    audioService.playClick();
  };

  const renderGame = () => {
    const commonProps = { 
      onGameOver: handleGameOver, 
      isPlaying: isPlaying && !isPaused, 
      sfxVolume, 
      hapticFeedback,
      onScoreUpdate: handleScoreUpdate 
    };
    
    switch (game.id) {
      case 'fruit-vortex': return <FruitVortex {...commonProps} />;
      case 'word-builder': return <WordBuilder {...commonProps} isDarkMode={isDarkMode} />;
      case 'sudoku-lite': return <Sudoku {...commonProps} onGameOver={(s) => handleGameOver(s, true)} />;
      case 'memory-matrix': return <MemoryMatrix {...commonProps} />;
      case 'labyrinth': return <Labyrinth {...commonProps} />;
      case 'color-clash': return <ColorClash {...commonProps} />;
      case 'binary-dash': return <BinaryDash {...commonProps} />;
      case 'bit-master': return <BitMaster {...commonProps} />;
      case 'quick-math': return <QuickMath {...commonProps} />;
      case 'pattern-finder': return <PatternFinder {...commonProps} />;
      case 'grammar-guardian': return <GrammarGuardian {...commonProps} />;
      case 'resonance-breathing': return <ResonanceBreathing {...commonProps} />;
      case 'reaction-test': return <ReactionTest {...commonProps} />;
      case 'tetris': return <Tetris {...commonProps} />;
      case 'neon-racer': return <NeonRacer {...commonProps} />;
      case 'sky-strike': return <SkyStrike {...commonProps} />;
      case 'snake-arena': return <SnakeArena {...commonProps} />;
      default:
        return (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <i className={`fas ${game.icon} text-8xl mb-6 opacity-20 dark:text-white text-slate-900`}></i>
            <h2 className="text-3xl font-bold mb-4 dark:text-white text-slate-900">{game.name} is coming soon!</h2>
            <button onClick={handleClose} className="px-8 py-3 bg-white/10 rounded-xl">Back to Hub</button>
          </div>
        );
    }
  };

  return (
    <div className={`fixed inset-0 z-50 ${isDarkMode ? 'bg-[#020617]' : 'bg-slate-50'} flex flex-col transition-all duration-500 ${isClosing ? 'portal-exit' : 'portal-enter'}`}>
      <div className="absolute top-0 left-0 right-0 p-4 md:p-6 flex items-center justify-between z-20 pointer-events-none">
        <div className="flex items-center gap-4 pointer-events-auto">
          <button onClick={handleClose} className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/10 transition-all shadow-lg group hover:bg-white/20" title="Exit to Hub">
            <i className="fas fa-arrow-left text-slate-300 group-hover:text-white"></i>
          </button>
          
          {isPlaying && !showGameOver && (
            <button 
              onClick={() => {
                setIsPaused(prev => !prev);
                audioService.playClick();
              }} 
              className={`w-12 h-12 rounded-2xl ${isPaused ? 'bg-indigo-600 border-indigo-500' : 'bg-white/10 border-white/10'} backdrop-blur-md flex items-center justify-center border transition-all shadow-lg group hover:scale-105`} 
              title={isPaused ? "Resume Session" : "Pause Session"}
            >
              <i className={`fas ${isPaused ? 'fa-play text-white animate-pulse' : 'fa-pause text-indigo-300 group-hover:text-indigo-100'}`}></i>
            </button>
          )}

          <div className="flex items-center gap-3 bg-white/5 px-4 py-2 rounded-2xl border border-white/10 backdrop-blur-md shadow-xl">
            <Logo size={24} showGlow={false} />
            {isPlaying ? (
              <div className="flex items-center gap-4">
                <div className="h-4 w-px bg-white/20" />
                <div className="flex flex-col">
                  <span className="text-[8px] font-black text-indigo-500 uppercase tracking-widest leading-none mb-1">Live Score</span>
                  <span className="text-sm font-black text-white tabular-nums italic leading-none animate-in fade-in slide-in-from-top-1 duration-300">{currentScore.toLocaleString()}</span>
                </div>
              </div>
            ) : (
              <h2 className="text-sm font-black italic dark:text-white text-slate-900 uppercase tracking-tighter leading-none">{game.name}</h2>
            )}
          </div>
        </div>

        {activeChallenge && isPlaying && (
          <div className="flex items-center gap-3 bg-amber-500/10 border-2 border-amber-500/30 px-4 py-2 rounded-2xl backdrop-blur-md shadow-xl pointer-events-auto animate-in fade-in slide-in-from-right-4 duration-500">
            <i className="fas fa-swords text-amber-500 animate-pulse"></i>
            <div className="flex flex-col">
              <span className="text-[7px] font-black text-amber-500 uppercase tracking-widest leading-none mb-1">Target to Beat ({activeChallenge.creatorUsername})</span>
              <span className="text-xs font-black text-white tabular-nums italic leading-none">{activeChallenge.targetScore.toLocaleString()}</span>
            </div>
            {currentScore > activeChallenge.targetScore ? (
              <span className="text-[8px] font-bold text-white bg-emerald-500 px-1.5 py-0.5 rounded uppercase leading-none ml-1 animate-bounce"><i className="fas fa-check mr-0.5"></i> Beaten</span>
            ) : (
              <span className="text-[8px] font-bold text-slate-300 bg-rose-500/50 px-1.5 py-0.5 rounded uppercase leading-none ml-1">-{Math.max(0, activeChallenge.targetScore - currentScore).toLocaleString()}</span>
            )}
          </div>
        )}
      </div>

      <div className={`flex-1 flex flex-col items-center justify-center relative bg-grid-white/[0.02] ${isPlaying && game.id === 'tetris' ? 'overflow-hidden pt-16 pb-4' : 'overflow-y-auto pt-24 pb-12'} transition-all duration-700`}>
        {!isPlaying && !showGameOver ? (
          <div className="text-center p-8 glass-card rounded-[3rem] max-w-xl w-full border-indigo-500/30 shadow-2xl animate-in fade-in zoom-in-95 duration-700 border-2 my-auto mx-4">
            <div className={`w-24 h-24 mx-auto rounded-[2.2rem] bg-gradient-to-br ${game.color} flex items-center justify-center text-5xl mb-6 shadow-2xl shadow-indigo-500/40 transform -rotate-3 hover:rotate-0 transition-transform duration-500`}>
              <i className={`fas ${game.icon} text-white`}></i>
            </div>
            <h1 className="text-4xl font-black mb-3 tracking-tighter italic uppercase dark:text-white text-slate-900 transition-colors">{game.name}</h1>
            <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed font-medium px-4">{game.description}</p>
            
            <div className="mb-8 text-left bg-slate-100 dark:bg-white/5 p-6 rounded-3xl border border-slate-200 dark:border-white/10">
              <h3 className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                <i className="fas fa-info-circle"></i> Mission Briefing
              </h3>
              <ul className="space-y-3">
                {game.instructions.map((inst, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm font-medium text-slate-700 dark:text-slate-300">
                    <span className="w-5 h-5 rounded-full bg-indigo-500/20 text-indigo-500 flex items-center justify-center text-[10px] font-black shrink-0 mt-0.5">{idx + 1}</span>
                    {inst}
                  </li>
                ))}
              </ul>
            </div>

            <button 
              onClick={() => { 
                setIsPlaying(true); 
                setIsPaused(false);
                setCurrentScore(0); 
                audioService.playClick();
              }}
              className="w-full py-5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-[2rem] font-black text-2xl hover:scale-105 active:scale-95 transition-all shadow-xl shadow-indigo-500/40 uppercase italic tracking-tighter"
            >
              START SESSION
            </button>
          </div>
        ) : showGameOver ? (
          <div className="text-center p-8 glass-card rounded-[3rem] max-w-xl w-full border-indigo-500/30 shadow-2xl transition-all animate-in fade-in zoom-in-95 duration-700 border-2 my-auto mx-4 overflow-hidden relative">
            {(isVictory || (activeChallenge && currentScore > activeChallenge.targetScore)) && (
              <div className="absolute inset-0 pointer-events-none">
                <VictoryEffect onComplete={() => {}} />
              </div>
            )}
            
            {activeChallenge ? (
              <>
                <div className={`w-20 h-20 mx-auto rounded-[1.8rem] bg-gradient-to-br ${currentScore > activeChallenge.targetScore ? 'from-amber-400 to-amber-600 shadow-[0_0_20px_rgba(245,158,11,0.4)]' : 'from-rose-500 to-rose-700'} flex items-center justify-center text-4xl mb-6 shadow-2xl ${currentScore > activeChallenge.targetScore ? 'animate-bounce' : ''}`}>
                  <i className={`fas ${currentScore > activeChallenge.targetScore ? 'fa-crown text-white' : 'fa-swords text-white'}`}></i>
                </div>
                
                <h1 className={`text-4xl font-black mb-2 tracking-tighter italic uppercase ${currentScore > activeChallenge.targetScore ? 'text-amber-500' : 'text-rose-500'}`}>
                  {currentScore > activeChallenge.targetScore ? 'DUEL VICTORIOUS!' : 'DUEL DEFEATED'}
                </h1>
                
                <p className="text-slate-600 dark:text-slate-400 text-sm font-medium mb-6 px-4">
                  {currentScore > activeChallenge.targetScore 
                    ? `Astonishing! You beat ${activeChallenge.creatorUsername}'s record score in ${game.name}!` 
                    : `You gave your best, but you didn't conquer ${activeChallenge.creatorUsername}'s target.`}
                </p>

                <div className="bg-slate-100 dark:bg-white/5 rounded-3xl p-8 border border-slate-200 dark:border-white/5 mb-8 shadow-inner text-center">
                  <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500 block mb-1">Your Score Achieved</span>
                  <p className={`text-6xl font-black tabular-nums drop-shadow-sm italic mb-4 ${currentScore > activeChallenge.targetScore ? 'text-amber-500' : 'text-indigo-500'}`}>
                    {currentScore.toLocaleString()}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 border-t border-slate-200 dark:border-white/10 pt-4">
                    <div className="flex flex-col">
                      <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Target to beat</span>
                      <span className="text-lg font-black text-rose-500 tabular-nums italic">{activeChallenge.targetScore.toLocaleString()}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[8px] font-black text-indigo-500 uppercase tracking-widest">Opened By</span>
                      <span className="text-sm font-black text-slate-700 dark:text-slate-300 truncate block mt-1">{activeChallenge.creatorUsername}</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className={`w-20 h-20 mx-auto rounded-[1.8rem] bg-gradient-to-br ${isVictory ? 'from-emerald-400 to-teal-600' : 'from-rose-500 to-rose-700'} flex items-center justify-center text-4xl mb-6 shadow-2xl animate-bounce`}>
                  <i className={`fas ${isVictory ? 'fa-trophy' : 'fa-skull'} text-white`}></i>
                </div>
                <h1 className={`text-5xl font-black mb-2 tracking-tighter italic uppercase ${isVictory ? 'text-emerald-500' : 'text-rose-500'}`}>{isVictory ? 'SUCCESS!' : 'GAME OVER'}</h1>
                
                <div className="bg-slate-100 dark:bg-white/5 rounded-3xl p-8 border border-slate-200 dark:border-white/5 mb-8 shadow-inner">
                  <p className="text-[10px] font-black text-slate-500 tracking-[0.3em] mb-2">Total Score Gained</p>
                  <p className={`text-6xl font-black tabular-nums drop-shadow-sm italic mb-4 ${isVictory ? 'text-emerald-500' : 'text-indigo-500'}`}>{currentScore.toLocaleString()}</p>
                  
                  <div className="grid grid-cols-2 gap-4 border-t border-slate-200 dark:border-white/10 pt-4">
                    <div className="flex flex-col">
                      <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Personal Best</span>
                      <span className="text-lg font-black text-indigo-500 tabular-nums italic">{Math.max(highScore, currentScore).toLocaleString()}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[8px] font-black text-amber-500 uppercase tracking-widest">Global Record</span>
                      <span className="text-lg font-black text-amber-500 tabular-nums italic">{globalRecord !== undefined ? Math.max(globalRecord, currentScore).toLocaleString() : '---'}</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="flex flex-col gap-3">
              {isAnonymous && currentScore > 0 && !activeChallenge && (
                <button onClick={onOpenProfile} className="w-full py-4 bg-emerald-500/20 border-2 border-emerald-500/40 text-emerald-500 rounded-2xl font-black text-sm mb-2 flex items-center justify-center gap-3 animate-pulse">
                  <i className="fas fa-cloud-arrow-up"></i> SECURE PROGRESS TO PROFILE
                </button>
              )}
              <button onClick={handleRetry} className="w-full py-5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl font-black text-xl hover:scale-105 active:scale-95 transition-all shadow-xl uppercase italic tracking-tighter">
                {activeChallenge ? 'RETRY DUEL SESSION' : 'RETRY SESSION'}
              </button>
              {!activeChallenge && (
                <button onClick={onViewLeaderboard} className="w-full py-4 bg-indigo-500/10 border border-indigo-500/20 text-indigo-500 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-indigo-500/20 transition-all">
                  <i className="fas fa-trophy mr-2"></i> View Global Leaderboard
                </button>
              )}
              <button onClick={handleClose} className="py-4 bg-white/5 border border-white/10 text-slate-500 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-rose-500/10 transition-all">
                {activeChallenge ? 'Close Duel & Return Hub' : 'Close Session'}
              </button>
            </div>
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center animate-in fade-in zoom-in-95 duration-700">{renderGame()}</div>
        )}
      </div>

      {/* Translucent Pause Menu Overlay */}
      {isPaused && (
        <div className="fixed inset-0 bg-slate-950/85 backdrop-blur-md z-40 flex items-center justify-center animate-in fade-in duration-300 pointer-events-auto">
          <div className="text-center p-8 glass-card rounded-[2.5rem] max-w-sm w-full border-2 border-indigo-500/30 shadow-2xl mx-4 transform animate-in zoom-in-95 duration-300 relative overflow-hidden flex flex-col items-center">
            {/* Ambient Background Glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 -z-10 pointer-events-none" />
            
            <div className="w-20 h-20 rounded-[1.8rem] bg-indigo-500/20 text-indigo-400 flex items-center justify-center text-3xl mb-6 shadow-inner animate-pulse">
              <i className="fas fa-pause"></i>
            </div>
            
            <h2 className="text-3xl font-black mb-2 tracking-tight uppercase italic dark:text-white text-slate-100">
              SESSION PAUSED
            </h2>
            <p className="text-sm text-slate-400 mb-8 font-medium px-4">
              Take a breath, adjust your focus, and jump right back in when you're ready.
            </p>
            
            <div className="flex flex-col gap-3 w-full">
              {/* Resume Button */}
              <button 
                onClick={() => {
                  setIsPaused(false);
                  audioService.playClick();
                }}
                className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-2xl font-black text-lg hover:scale-[1.02] active:scale-95 transition-all shadow-xl shadow-indigo-500/30 uppercase italic tracking-tighter"
              >
                <i className="fas fa-play mr-2 text-sm"></i> RESUME SESSION
              </button>
              
              {/* Restart Button */}
              <button 
                onClick={handleRetry}
                className="w-full py-3.5 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-300 border border-indigo-500/20 rounded-2xl font-bold text-base hover:scale-[1.02] active:scale-95 transition-all uppercase tracking-tight"
              >
                <i className="fas fa-sync-alt mr-2 text-sm"></i> Restart Game
              </button>
              
              {/* Return to Hub Button */}
              <button 
                onClick={handleClose}
                className="w-full py-3.5 bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 rounded-2xl font-bold text-base hover:scale-[1.02] active:scale-95 transition-all uppercase tracking-tight"
              >
                <i className="fas fa-home mr-2 text-sm"></i> Return to Hub
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameRunner;
